<template>
  <div>
    <!-- Tabs -->
    <v-tabs
      v-model="tab"
      color="primary"
      density="compact"
    >
      <v-tab value="table">Transaktionen</v-tab>
      <v-tab value="chart">Delta € je Aktie</v-tab>
      <v-tab value="pivot">Pivot</v-tab>
      <v-tab value="strategy">Vergleich Strategie</v-tab>
      <v-tab value="bankCompare">Banking Vergleich</v-tab>
      <v-tab value="upload">SteuerWisoUpload</v-tab>
    </v-tabs>

    <v-divider></v-divider>

    <!-- Tab Content -->
    <v-tabs-window v-model="tab">
      <!-- TAB 1: TABELLE -->
      <v-tabs-window-item value="table">
        <v-card class="mt-2">
          <div class="d-flex align-center justify-end flex-wrap ga-2 pa-3 pb-0">
            <span class="text-subtitle-1 font-weight-bold mr-2" aria-live="polite">
              Cash: {{ cashCurrentEuro }}
            </span>
            <v-btn
              color="primary"
              variant="tonal"
              prepend-icon="mdi-refresh"
              :loading="transactionsLoading"
              :disabled="transactionsLoading"
              @click="reloadTransactions"
            >
              Aktualisieren
            </v-btn>
            <v-btn
              color="primary"
              variant="tonal"
              :disabled="!Array.isArray(transactions) || transactions.length === 0"
              @click="downloadTransactionsCsv"
            >
              Download CSV
            </v-btn>
          </div>

          <v-alert
            v-if="transactionsError"
            type="error"
            variant="tonal"
            class="ma-3"
            role="alert"
          >
            {{ transactionsError }}
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="filteredTransactions"
            :row-props="transactionRowProps"
            :loading="transactionsLoading"
            loading-text="Transaktionen werden geladen …"
            :search="search"
            :items-per-page="40"
            :sort-by="[{ key: 'idstocks_transactions', order: 'desc' }]"
            fixed-header
            height="70vh"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, 100]
            }"
            density="compact"
            @click:row="handleClickActivty"
            class="elevation-1 text-caption tx-table"
          >
            <template #header.stocks_transactions_category_label>
              <div class="tx-header-filter">
                <div class="tx-header-filter__title">Typ</div>
                <v-select
                  v-model="transactionTypeFilter"
                  :items="transactionTypeOptions"
                  density="compact"
                  variant="underlined"
                  hide-details
                  clearable
                  placeholder="Alle Typen"
                  class="tx-header-filter__input"
                  @click.stop
                  @keydown.stop
                />
              </div>
            </template>

            <template #header.stocks_transactions_stocks_name>
              <div class="tx-header-filter">
                <div class="tx-header-filter__title">Stock Name</div>
                <v-text-field
                  v-model="stockNameFilter"
                  density="compact"
                  variant="underlined"
                  hide-details
                  clearable
                  placeholder="Filter"
                  class="tx-header-filter__input"
                  @click.stop
                  @keydown.stop
                />
              </div>
            </template>

            <template #item.stocks_transactions_preis="{ item }">
              <span v-if="Number(item.stocks_transactions_category) === 2">{{ item.stocks_transactions_preis }}</span>
              <span v-else>—</span>
            </template>

            <template v-slot:item.stocks_transactions_datum="{ item }">
              <div>
                {{ formatDateTime(item.stocks_transactions_datum) }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-tabs-window-item>

      <!-- TAB 1: BAR CHART -->
      <v-tabs-window-item value="chart">
        <v-card class="mt-2">
          <v-btn @click="queueBarChartRender">render</v-btn>
          <v-responsive height="1600" :aspect-ratio="1">
            <canvas ref="barCanvas"></canvas>
          </v-responsive>
        </v-card>
      </v-tabs-window-item>

      <!-- TAB 2: PIVOT -->
      <v-tabs-window-item value="pivot">
        <v-card class="mt-2">
          <div id="pivot3" class="pivot-scroll">
            <VuePivottableUi
              :data="pivotTransactions"
              aggregatorName="Sum"
              :rows="['stocks_transactions_stocks_name']"
              :cols="['stocks_transactions_category_label']"
              :vals="['stocks_transactions_preis']"
            />
          </div>
        </v-card>
      </v-tabs-window-item>

      <!-- TAB 4: VERGLEICH STRATEGIE -->
      <v-tabs-window-item value="strategy">
        <v-card class="mt-2 pa-4">
          <div class="d-flex align-center justify-space-between mb-4 flex-wrap ga-3">
            <div>
              <div class="text-h6 mb-1">Strategie-Vergleich</div>
              <div class="text-caption">
                Quelle: <code>/deltapreis/all</code>. Prozentwerte beziehen sich auf den extern benoetigten Kapitalbedarf, nicht auf die Summe aller Brutto-Kaeufe.
              </div>
            </div>

            <v-btn
              color="primary"
              variant="tonal"
              :loading="strategyLoading"
              @click="loadStrategyComparison"
            >
              Neu laden
            </v-btn>
          </div>

          <v-alert
            v-if="strategyError"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ strategyError }}
          </v-alert>

          <v-card
            variant="outlined"
            class="mb-4"
          >
            <v-card-title class="text-subtitle-1">
              Pivot High Quality
            </v-card-title>

            <v-card-text>
              <div class="text-caption mb-3">
                Nur Zeilen mit <code>result_quality = high</code>
              </div>

              <v-table density="compact">
                <thead>
                  <tr>
                    <th>Kennzahl</th>
                    <th class="text-right">Aktiv Ergebnis</th>
                    <th class="text-right">Buy & Hold Ergebnis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Summe Netto-Ergebnis</td>
                    <td class="text-right" :class="numberClass(strategyPivotHigh.actual_sum)">
                      {{ formatCurrency(strategyPivotHigh.actual_sum) }}
                    </td>
                    <td class="text-right" :class="numberClass(strategyPivotHigh.benchmark_sum)">
                      {{ formatCurrency(strategyPivotHigh.benchmark_sum) }}
                    </td>
                  </tr>
                  <tr>
                    <td>Anzahl bessere Strategie</td>
                    <td class="text-right">
                      {{ strategyPivotHigh.actual_wins }}
                    </td>
                    <td class="text-right">
                      {{ strategyPivotHigh.benchmark_wins }}
                    </td>
                  </tr>
                  <tr>
                    <td>Outperformance Summe</td>
                    <td class="text-right" :class="numberClass(strategyPivotHigh.outperformance_sum)">
                      {{ formatCurrency(strategyPivotHigh.outperformance_sum) }}
                    </td>
                    <td class="text-right" :class="numberClass(-strategyPivotHigh.outperformance_sum)">
                      {{ formatCurrency(-strategyPivotHigh.outperformance_sum) }}
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <div class="text-caption mt-3">
                High-Quality Datensätze: {{ strategyPivotHigh.count }}
              </div>
            </v-card-text>
          </v-card>

          <v-data-table
            :headers="strategyHeaders"
            :items="strategyRows"
            :loading="strategyLoading"
            :items-per-page="25"
            fixed-header
            height="70vh"
            density="compact"
            class="elevation-1 text-caption strategy-comparison-table"
            @click:row="handleClickStrategyRow"
          >
            <template #item.latest_price="{ item }">
              {{ formatCurrency(item.latest_price) }}
            </template>

            <template #item.latest_price_timestamp="{ item }">
              {{ formatDate(item.latest_price_timestamp) }}
            </template>

            <template #item.analysis_start_date="{ item }">
              {{ formatDate(item.analysis_start_date) }}
            </template>

            <template #item.actual_net_result="{ item }">
              <span :class="numberClass(item.actual_net_result)">
                {{ formatCurrency(item.actual_net_result) }}
              </span>
            </template>

            <template #item.benchmark_net_result="{ item }">
              <span :class="numberClass(item.benchmark_net_result)">
                {{ formatCurrency(item.benchmark_net_result) }}
              </span>
            </template>

            <template #item.outperformance_eur="{ item }">
              <span :class="numberClass(item.outperformance_eur)">
                {{ formatCurrency(item.outperformance_eur) }}
              </span>
            </template>

            <template #item.outperformance_pct_points="{ item }">
              <span :class="numberClass(item.outperformance_pct_points)">
                {{ formatPercentPoint(item.outperformance_pct_points) }}
              </span>
            </template>

            <template #item.better_strategy="{ item }">
              <v-chip
                :color="item.better_strategy === 'actual_strategy' ? 'success' : item.better_strategy === 'buy_and_hold_benchmark' ? 'warning' : 'default'"
                size="small"
                variant="flat"
              >
                {{ formatStrategyLabel(item.better_strategy) }}
              </v-chip>
            </template>

            <template #item.data_quality_notes="{ item }">
              <v-tooltip
                v-if="Array.isArray(item.data_quality_notes) && item.data_quality_notes.length"
                location="top"
              >
                <template #activator="{ props }">
                  <span v-bind="props">
                    {{ item.data_quality_notes.join(' | ').slice(0, 10) }}
                  </span>
                </template>
                {{ item.data_quality_notes.join(' | ') }}
              </v-tooltip>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </v-card>
      </v-tabs-window-item>

      <!-- TAB 5: BANKING VERGLEICH -->
      <v-tabs-window-item value="bankCompare">
        <v-card class="mt-2 pa-4">
          <div class="d-flex align-center justify-space-between mb-4 flex-wrap ga-3">
            <div>
              <div class="text-h6 mb-1">Banking-App Export vergleichen</div>
              <div class="text-caption">
                Vergleicht den CSV-Export der Banking-App mit den geladenen Transaktionen anhand von Datum, Betrag und Text.
                Beruecksichtigt werden nur Bank-Zwecke mit SellOrder, BuyOrder, Tax correction oder StopSell.
              </div>
            </div>

            <v-btn
              color="primary"
              variant="tonal"
              :disabled="!bankCompareRows.length"
              @click="downloadBankCompareCsv"
            >
              Vergleich CSV
            </v-btn>
          </div>

          <v-row>
            <v-col cols="12" md="5">
              <v-file-input
                v-model="bankCompareFile"
                label="Banking CSV Export"
                accept=".csv,text/csv"
                variant="outlined"
                prepend-icon="mdi-bank-transfer"
                show-size
                clearable
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="bankCompareTolerance"
                label="Betrag-Toleranz"
                type="number"
                step="0.01"
                variant="outlined"
              />
            </v-col>

            <v-col cols="12" md="5" class="d-flex align-center ga-3">
              <v-btn
                color="primary"
                :loading="bankCompareLoading"
                :disabled="!bankCompareFile || bankCompareLoading"
                @click="runBankCompare"
              >
                Vergleich starten
              </v-btn>

              <v-btn
                variant="outlined"
                :disabled="bankCompareLoading"
                @click="resetBankCompare"
              >
                Zuruecksetzen
              </v-btn>
            </v-col>
          </v-row>

          <v-alert
            v-if="bankCompareError"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ bankCompareError }}
          </v-alert>

          <v-row v-if="bankCompareSummary" class="mb-4">
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="pa-3">
                <div class="text-caption">Bank-Zeilen</div>
                <div class="text-h6">{{ bankCompareSummary.bankRows }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="pa-3">
                <div class="text-caption">Exakte Treffer</div>
                <div class="text-h6 text-success">{{ bankCompareSummary.exactMatches }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="pa-3">
                <div class="text-caption">Abweichungen</div>
                <div class="text-h6 text-warning">{{ bankCompareSummary.partialMatches }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="pa-3">
                <div class="text-caption">Nicht gefunden</div>
                <div class="text-h6 text-error">{{ bankCompareSummary.missingInApp }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="pa-3">
                <div class="text-caption">Nicht im Bank-Export</div>
                <div class="text-h6 text-error">{{ bankCompareSummary.missingInBank }}</div>
              </v-card>
            </v-col>
          </v-row>

          <v-data-table
            :headers="bankCompareHeaders"
            :items="filteredBankCompareRows"
            :sort-by="[{ key: 'bankDate', order: 'desc' }]"
            :items-per-page="50"
            fixed-header
            height="70vh"
            density="compact"
            class="elevation-1 text-caption bank-compare-table"
            @click:row="handleClickBankCompareRow"
          >
            <template #item.status="{ item }">
              <v-chip
                :color="bankCompareStatusColor(item.status)"
                size="small"
                variant="flat"
                @click.stop="handleClickBankCompareRow(null, item)"
              >
                {{ item.status }}
              </v-chip>
            </template>

            <template #header.appName>
              <div class="tx-header-filter">
                <div class="tx-header-filter__title">App Name</div>
                <v-text-field
                  v-model="bankCompareAppNameFilter"
                  density="compact"
                  variant="underlined"
                  hide-details
                  clearable
                  placeholder="Filter"
                  class="tx-header-filter__input"
                  @click.stop
                  @keydown.stop
                />
              </div>
            </template>

            <template #header.bankPurpose>
              <div class="tx-header-filter">
                <div class="tx-header-filter__title">Bank Zweck</div>
                <v-text-field
                  v-model="bankCompareBankPurposeFilter"
                  density="compact"
                  variant="underlined"
                  hide-details
                  clearable
                  placeholder="Filter"
                  class="tx-header-filter__input"
                  @click.stop
                  @keydown.stop
                />
              </div>
            </template>

            <template #item.bankAmount="{ item }">
              {{ formatCurrency(item.bankAmount) }}
            </template>

            <template #item.appAmount="{ item }">
              {{ item.appAmount === null ? '-' : formatCurrency(item.appAmount) }}
            </template>

            <template #item.diff="{ item }">
              <span :class="numberClass(-Math.abs(item.diff || 0))">
                {{ item.diff === null ? '-' : formatCurrency(item.diff) }}
              </span>
            </template>
          </v-data-table>

          <v-dialog v-model="bankCreateDialog" max-width="760">
            <v-card>
              <v-card-title>Bank-Transaktion anlegen</v-card-title>

              <v-card-text>
                <v-alert
                  v-if="bankCreateError"
                  type="error"
                  variant="tonal"
                  class="mb-4"
                >
                  {{ bankCreateError }}
                </v-alert>

                <v-row v-if="bankCreateTx" dense>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="bankCreateTx.stocks_idstocks"
                      :items="stockOptions"
                      item-title="stocks_name"
                      item-value="idstocks"
                      label="Stock"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      :model-value="suggestedBankCreateStockName"
                      label="Vorschlag aus Bank Zweck"
                      readonly
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="bankCreateTx.stocks_transactions_category"
                      label="Typ"
                      type="number"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="bankCreateTx.stocks_menge"
                      label="Menge"
                      type="number"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="bankCreateTx.stocks_gebuehr"
                      label="Gebuehr"
                      type="number"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="bankCreateTx.stocks_transactions_preisprostueck"
                      label="Preis pro Stueck"
                      type="number"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="bankCreateTx.stocks_transactions_preis"
                      label="Preis"
                      type="number"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="bankCreateTx.stocks_transactions_preisV02"
                      label="Preis V02"
                      type="number"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="bankCreateTx.stocks_transactions_datum"
                      label="Datum"
                      type="date"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>

                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="bankCreateTx.stocks_transactions_beschreibung"
                      label="Beschreibung"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  variant="text"
                  :disabled="bankCreateSaving"
                  @click="closeBankCreateDialog"
                >
                  Schliessen
                </v-btn>
                <v-btn
                  color="primary"
                  variant="flat"
                  :loading="bankCreateSaving"
                  @click="saveBankMissingTransaction"
                >
                  Speichern ohne Mengenanpassung
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </v-tabs-window-item>

      <!-- TAB 6: URLADUNG -->
      <v-tabs-window-item value="upload">
        <TaxCsvTransactionImport @imported="handleTaxCsvImported" />
      </v-tabs-window-item>
    </v-tabs-window>

              <!-- DETAILS DIALOG -->
          <v-dialog v-model="detailsDialog" max-width="720">
            <v-card>
              <v-card-title>Transaktion bearbeiten</v-card-title>

              <v-card-text v-if="selectedTx">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :model-value="selectedTx.stocks_transactions_stocks_name || '-'"
                      label="Stock Name"
                      readonly
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      :model-value="selectedTxId || '-'"
                      label="Transaktions-ID"
                      readonly
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedTx.stocks_transactions_category"
                      label="Typ"
                      type="number"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedTx.stocks_menge"
                      label="Menge"
                      type="number"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedTx.stocks_gebuehr"
                      label="Gebühr"
                      type="number"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedTx.stocks_transactions_preisprostueck"
                      label="Preis pro Stück"
                      type="number"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedTx.stocks_transactions_preis"
                      label="Preis"
                      type="number"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedTx.stocks_transactions_preisV02"
                      label="Preis V02"
                      type="number"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedTx.stocks_transactions_datum"
                      label="Datum"
                      type="date"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editedTx.stocks_transactions_beschreibung"
                      label="Beschreibung"
                    />
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  color="error"
                  variant="tonal"
                  prepend-icon="mdi-delete"
                  :loading="detailsDeleting"
                  :disabled="detailsSaving"
                  @click="deleteSelectedTransaction"
                >
                  Löschen (achtung Mengen werden nicht angepasst)
                </v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  variant="flat"
                  :loading="detailsSaving"
                  :disabled="detailsDeleting"
                  @click="saveSelectedTransaction"
                >
                  Speichern
                </v-btn>
                <v-btn variant="text" @click="closeDetailsDialog">Schliessen</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
  </div>
</template>

<script>
import axios from '@/api/client'
import { mapActions, mapGetters } from 'vuex'
import { VuePivottableUi } from 'vue3-pivottable'
import TaxCsvTransactionImport from '@/components/TaxCsvTransactionImport.vue'
import {
  Chart,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js'

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title)

export default {
  components: {
    VuePivottableUi,
    TaxCsvTransactionImport,
  },

  data: () => ({
    tab: 'table',
    search: '',
    stockNameFilter: '',
    transactionTypeFilter: null,
    transactionsLoading: false,
    transactionsError: '',
    chartBar: null,
    chartRenderFrame: null,

    // âœ… NEU: Dialog State
    detailsDialog: false,
    selectedTx: null,
    editedTx: null,
    detailsSaving: false,
    detailsDeleting: false,

    // âœ… NEU: Upload State
    strategyLoading: false,
    strategyError: '',
    strategyRows: [],
    bankCompareFile: null,
    bankCompareLoading: false,
    bankCompareError: '',
    bankCompareRows: [],
    bankCompareSummary: null,
    bankCompareTolerance: 100,
    bankCompareAppNameFilter: '',
    bankCompareBankPurposeFilter: '',
    bankCreateDialog: false,
    bankCreateRow: null,
    bankCreateTx: null,
    bankCreateSaving: false,
    bankCreateError: '',
    suggestedBankCreateStock: null,

    headers: [
      { title: 'ID', align: 'start', key: 'idstocks_transactions' },
      { title: 'Typ', fixed: true, key: 'stocks_transactions_category_label', align: 'start' },
      { title: 'Stock Name', align: 'start', key: 'stocks_transactions_stocks_name' },
      { title: 'Menge', align: 'start', key: 'stocks_menge' },
      { title: 'G/V', align: 'start', key: 'stocks_transactions_preis' },
      { title: 'Betrag (€)', align: 'start', key: 'stocks_transactions_preisV02' },
      { title: 'Datum', align: 'start', key: 'stocks_transactions_datum' },
      { title: 'Beschreibung', align: 'start', key: 'stocks_transactions_beschreibung' },
    ],


    strategyHeaders: [
      { title: 'Aktie', key: 'stock_name' },
      { title: 'Letzter Kurs', key: 'latest_price', align: 'end' },
      { title: 'Stand', key: 'latest_price_timestamp' },
      { title: 'Analyse ab', key: 'analysis_start_date' },
      { title: 'Aktiv Ergebnis', key: 'actual_net_result', align: 'end' },
      { title: 'Buy & Hold Ergebnis', key: 'benchmark_net_result', align: 'end' },
      { title: 'Differenz', key: 'outperformance_eur', align: 'end' },
      { title: 'Diff Rendite-Punkte', key: 'outperformance_pct_points', align: 'end' },
      { title: 'Bessere Strategie', key: 'better_strategy' },
      { title: 'Trades', key: 'trade_count', align: 'end' },
      { title: 'Qualität', key: 'result_quality' },
      { title: 'Hinweise', key: 'data_quality_notes' },
    ],

    bankCompareHeaders: [
      { title: 'Status', key: 'status' },
      { title: 'Bank Datum', key: 'bankDate' },
      { title: 'Bank Empfaenger', key: 'bankName' },
      { title: 'Bank Zweck', key: 'bankPurpose' },
      { title: 'Bank Betrag', key: 'bankAmount', align: 'end' },
      { title: 'App ID', key: 'appId', align: 'end' },
      { title: 'App Datum', key: 'appDate' },
      { title: 'App Name', key: 'appName' },
      { title: 'App Typ', key: 'appCategory', align: 'end' },
      { title: 'App Betrag', key: 'appAmount', align: 'end' },
      { title: 'Diff', key: 'diff', align: 'end' },
      { title: 'Hinweis', key: 'note' },
    ],

    pagination: {
      rowsPerPage: -1,
    },
  }),

  computed: {
    ...mapGetters({
      transactions: 'stateTransactions',
      stocks: 'stateStocks',
      user: 'stateUser',
    }),

    cashCurrentEuro () {
      const cash = (Array.isArray(this.stocks) ? this.stocks : []).find((stock) =>
        String(stock?.stocks_name || '').toLowerCase() === '0_ccash'
      )
      const value = cash?.stock_Aktuellerkurs ?? cash?.stock_Aktuellerkurs_gesamt
      if (value == null || value === '' || !Number.isFinite(Number(value))) return '-'
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value))
    },

    currentUserId () {
      return Number(this.user?.iduser ?? 0)
    },


    stockOptions () {
      const currentUserId = this.currentUserId
      return (Array.isArray(this.stocks) ? this.stocks : [])
        .filter((stock) => !currentUserId || this.getStockUserId(stock) === currentUserId)
        .map((stock) => ({
          idstocks: Number(stock?.idstocks ?? 0),
          stocks_name: String(stock?.stocks_name || ''),
        }))
        .filter((stock) => stock.idstocks && stock.stocks_name)
        .sort((a, b) => a.stocks_name.localeCompare(b.stocks_name))
    },

    suggestedBankCreateStockName () {
      return this.suggestedBankCreateStock?.stocks_name || ''
    },

    transactionTypeOptions () {
      return [...new Map(this.pivotTransactions.map(item => [
        Number(item.stocks_transactions_category),
        { title: item.stocks_transactions_category_label, value: Number(item.stocks_transactions_category) },
      ])).values()].sort((a, b) => a.value - b.value)
    },

    filteredTransactions () {
      const filter = String(this.stockNameFilter || '').trim().toLowerCase()
      return this.pivotTransactions.filter(item =>
        (this.transactionTypeFilter == null || Number(item.stocks_transactions_category) === this.transactionTypeFilter) &&
        (!filter || String(item.stocks_transactions_stocks_name || '').toLowerCase().includes(filter))
      )
    },

    pivotTransactions () {
      const categoryLabels = {
        1: 'Kauf',
        2: 'Verkauf',
        3: 'Dividende',
        4: 'Gewinn/Verlust',
        5: 'Cash Einzahlung',
        6: 'Cash Auszahlung',
      }

      return (Array.isArray(this.transactions) ? this.transactions : []).map((transaction) => {
        const category = Number(transaction?.stocks_transactions_category)
        return {
          ...transaction,
          stocks_transactions_category_label: categoryLabels[category] || `Typ ${transaction?.stocks_transactions_category ?? '-'}`,
        }
      })
    },

    selectedTxId () {
      return this.extractTransactionId(this.selectedTx)
    },

    filteredBankCompareRows () {
      const list = Array.isArray(this.bankCompareRows) ? this.bankCompareRows : []
      const appNameFilter = String(this.bankCompareAppNameFilter || '').trim().toLowerCase()
      const bankPurposeFilter = String(this.bankCompareBankPurposeFilter || '').trim().toLowerCase()

      return list.filter((item) => {
        const appNameMatches = !appNameFilter ||
          String(item?.appName || '')
            .toLowerCase()
            .includes(appNameFilter)

        const bankPurposeMatches = !bankPurposeFilter ||
          String(item?.bankPurpose || '')
            .toLowerCase()
            .includes(bankPurposeFilter)

        return appNameMatches && bankPurposeMatches
      })
    },

    selectedTxEntries () {
      if (!this.selectedTx || typeof this.selectedTx !== 'object') return []

      return Object.entries(this.selectedTx)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => ({ key, value }))
    },

    strategyPivotHigh () {
      const highRows = Array.isArray(this.strategyRows)
        ? this.strategyRows.filter((row) => String(row?.result_quality || '').toLowerCase() === 'high')
        : []

      return highRows.reduce((acc, row) => {
        acc.count += 1
        acc.actual_sum += Number(row?.actual_net_result ?? 0)
        acc.benchmark_sum += Number(row?.benchmark_net_result ?? 0)
        acc.outperformance_sum += Number(row?.outperformance_eur ?? 0)

        if (row?.better_strategy === 'actual_strategy') acc.actual_wins += 1
        if (row?.better_strategy === 'buy_and_hold_benchmark') acc.benchmark_wins += 1

        return acc
      }, {
        count: 0,
        actual_sum: 0,
        benchmark_sum: 0,
        outperformance_sum: 0,
        actual_wins: 0,
        benchmark_wins: 0,
      })
    },
  },

  watch: {
    tab (value) {
      if (value === 'chart') this.queueBarChartRender()
    },

    transactions () {
      if (this.tab === 'chart') this.queueBarChartRender()
    },
  },

  mounted () {
    console.log('Bin in TransactionScreen')
    this.reloadTransactions()
    this.loadStrategyComparison()
  },

  beforeUnmount () {
    if (this.chartRenderFrame !== null) {
      window.cancelAnimationFrame(this.chartRenderFrame)
      this.chartRenderFrame = null
    }
    if (this.chartBar) {
      this.chartBar.destroy()
      this.chartBar = null
    }
  },

  methods: {
    ...mapActions(['updateTransaction', 'deleteTransaction', 'getStocks']),

    transactionRowProps ({ item }) {
      const type = Number(item?.stocks_transactions_category)
      return { class: type === 1 ? 'tx-row-buy' : type === 2 ? 'tx-row-sell' : '' }
    },

    async reloadTransactions () {
      if (this.transactionsLoading) return

      this.transactionsLoading = true
      this.transactionsError = ''
      try {
        await Promise.all([
          this.$store.dispatch('getTransactions'),
          this.$store.dispatch('getStocks'),
        ])
      } catch (error) {
        console.error('reloadTransactions failed', error)
        this.transactionsError = 'Transaktionen oder Cash konnten nicht geladen werden. Bitte erneut versuchen.'
      } finally {
        this.transactionsLoading = false
      }
    },

    openFile (name) {
      console.log('Open:', name)
      window.open(name)
    },

    getAllTransactionsForExport () {
      return Array.isArray(this.transactions) ? this.transactions : []
    },

    getStockUserId (stock) {
      return Number(
        stock?.user_iduser?.iduser ??
        stock?.user_iduser_id ??
        stock?.user_iduser ??
        0
      )
    },

    formatGermanCsvNumber (value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return value ?? ''
      return String(num).replace('.', ',')
    },

    formatGermanCsvDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleDateString('de-DE')
    },

    getTransactionCsvValue (item, key) {
      if (key === 'stocks_transactions_datum') {
        return this.formatGermanCsvDate(item?.[key])
      }

      if ([
        'stocks_menge',
        'stocks_transactions_preis',
        'stocks_transactions_preisV02',
      ].includes(key)) {
        return this.formatGermanCsvNumber(item?.[key])
      }

      return item?.[key] ?? ''
    },

    escapeCsvCell (value) {
      const text = String(value ?? '')
      if (/[;"\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
      }
      return text
    },

    downloadTransactionsCsv () {
      const rows = this.getAllTransactionsForExport()
      if (!rows.length) return

      const exportHeaders = this.headers.map((header) => ({
        title: header.title,
        key: header.key === 'stocks_transactions_category_label' ? 'stocks_transactions_category' : header.key,
      }))

      const lines = [
        exportHeaders.map((header) => this.escapeCsvCell(header.title)).join(';'),
        ...rows.map((item) =>
          exportHeaders
            .map((header) => this.escapeCsvCell(this.getTransactionCsvValue(item, header.key)))
            .join(';')
        ),
      ]

      const csvContent = `\uFEFF${lines.join('\r\n')}`
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const today = new Date().toISOString().slice(0, 10)

      link.href = url
      link.download = `transaktionen_${today}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    resetBankCompare () {
      this.bankCompareFile = null
      this.bankCompareLoading = false
      this.bankCompareError = ''
      this.bankCompareRows = []
      this.bankCompareSummary = null
      this.bankCompareAppNameFilter = ''
      this.bankCompareBankPurposeFilter = ''
    },

    async runBankCompare () {
      this.bankCompareError = ''
      this.bankCompareRows = []
      this.bankCompareSummary = null

      if (!this.bankCompareFile) {
        this.bankCompareError = 'Bitte Banking-CSV auswaehlen.'
        return
      }

      try {
        this.bankCompareLoading = true
        const selectedFile = this.getSelectedFile(this.bankCompareFile)
        if (!selectedFile) {
          this.bankCompareError = 'Bitte Banking-CSV auswaehlen.'
          return
        }

        const csvText = await this.readFileAsText(selectedFile)
        let parsedRows = this.parseDelimitedCsv(csvText, ';')
        const tradeRepublicRows = this.parseDelimitedCsv(csvText, ',')
        const isTradeRepublic = tradeRepublicRows.length > 0 &&
          ['date', 'type', 'amount', 'name', 'currency'].every(key => key in tradeRepublicRows[0])
        if (isTradeRepublic) parsedRows = tradeRepublicRows
        if (!parsedRows.length) {
          this.bankCompareError = 'Keine Datenzeilen in der CSV erkannt. Erwartet wird eine CSV mit Semikolon als Trennzeichen und einer Kopfzeile.'
          return
        }
        const requiredHeaders = isTradeRepublic
          ? ['date', 'type', 'amount', 'name', 'currency']
          : ['wertstellung', 'betrag', 'verwendungszweck']
        const missingHeaders = requiredHeaders.filter(header => !(header in parsedRows[0]))
        if (missingHeaders.length) {
          this.bankCompareError = `CSV-Format nicht erkannt. Fehlende Spalten: ${missingHeaders.join(', ')}. Erkannte Spalten: ${Object.keys(parsedRows[0]).join(', ')}.`
          return
        }
        const supportedRows = isTradeRepublic
          ? parsedRows.filter(row => ['BUY', 'SELL', 'TAX_OPTIMIZATION'].includes(row.type))
          : parsedRows
        if (isTradeRepublic && supportedRows.some(row => row.currency !== 'EUR')) {
          this.bankCompareError = 'Der Trade-Republic-Vergleich unterstützt derzeit nur EUR-Buchungen. Der Export enthält andere Währungen.'
          return
        }
        const validRows = supportedRows
          .map(row => isTradeRepublic ? this.normalizeTradeRepublicCompareRow(row) : this.normalizeBankCompareRow(row))
          .filter(row => row.date && Number.isFinite(row.amount))
        const bankRows = validRows.filter(row => this.isWhitelistedBankPurpose(row.purpose))
        if (!bankRows.length) {
          this.bankCompareError = validRows.length
            ? `${validRows.length} Bank-Zeilen gelesen, aber keine mit SellOrder, BuyOrder, Tax correction oder StopSell im Verwendungszweck gefunden.`
            : `${parsedRows.length} CSV-Zeilen gelesen, aber kein gültiges Datum mit Betrag erkannt. Bitte Wertstellung und Betragsformat prüfen.`
          return
        }
        const bankDateRange = this.getDateRangeFromRows(bankRows)

        const appRows = (Array.isArray(this.transactions) ? this.transactions : [])
          .map(row => this.normalizeTransactionForBankCompare(row))
          .filter(row => row.date && Number.isFinite(row.amount))
          .filter(row => this.isDateInRange(row.date, bankDateRange))

        this.debugBankCompareMatch(bankRows, appRows, 'Broadcom01.06')
        this.debugBankCompareTransaction(bankRows, appRows, 2868, 'simon')

        this.bankCompareRows = await this.compareBankRowsWithTransactions(bankRows, appRows)
        this.debugBankCompareAppAssignment(bankRows, this.bankCompareRows, 2873, '2026-06-01')
        this.debugBankCompareResult(this.bankCompareRows, 2868, 'simon')
        this.bankCompareSummary = this.buildBankCompareSummary(this.bankCompareRows, bankRows.length, appRows.length)
      } catch (error) {
        console.error('runBankCompare failed', error)
        this.bankCompareError = error?.message || 'Banking-Vergleich fehlgeschlagen.'
      } finally {
        this.bankCompareLoading = false
      }
    },

    getSelectedFile (value) {
      if (Array.isArray(value)) return value[0] || null
      return value || null
    },

    getDateRangeFromRows (rows) {
      const dates = rows.map(row => row.date).filter(Boolean).sort()
      return {
        min: dates[0] || '',
        max: dates[dates.length - 1] || '',
      }
    },

    isDateInRange (date, range) {
      if (!date || !range?.min || !range?.max) return false
      return date >= range.min && date <= range.max
    },

    readFileAsText (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'))
        reader.readAsText(file, 'utf-8')
      })
    },

    parseDelimitedCsv (text, delimiter = ';') {
      const rows = []
      let row = []
      let cell = ''
      let inQuotes = false

      for (let index = 0; index < text.length; index += 1) {
        const char = text[index]
        const next = text[index + 1]

        if (char === '"' && inQuotes && next === '"') {
          cell += '"'
          index += 1
          continue
        }

        if (char === '"') {
          inQuotes = !inQuotes
          continue
        }

        if (char === delimiter && !inQuotes) {
          row.push(cell)
          cell = ''
          continue
        }

        if ((char === '\n' || char === '\r') && !inQuotes) {
          if (char === '\r' && next === '\n') index += 1
          row.push(cell)
          if (row.some(value => String(value).trim() !== '')) rows.push(row)
          row = []
          cell = ''
          continue
        }

        cell += char
      }

      row.push(cell)
      if (row.some(value => String(value).trim() !== '')) rows.push(row)

      if (rows.length < 2) return []

      const headers = rows[0].map(value => this.normalizeCsvHeader(value))
      return rows.slice(1).map(values => {
        const item = {}
        headers.forEach((header, index) => {
          item[header] = values[index] ?? ''
        })
        return item
      })
    },

    normalizeCsvHeader (value) {
      return String(value || '')
        .replace(/^\uFEFF/, '')
        .trim()
        .toLowerCase()
        .replace(/\u00e4/g, 'ae')
        .replace(/\u00f6/g, 'oe')
        .replace(/\u00fc/g, 'ue')
        .replace(/\u00df/g, 'ss')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    },
    parseGermanNumber (value) {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null
      }

      const text = String(value).trim()
      const normalized = text.includes(',')
        ? text.replace(/\./g, '').replace(',', '.')
        : text.replace(/\s/g, '')
      const numberValue = Number(normalized)
      return Number.isFinite(numberValue) ? numberValue : null
    },

    parseAnyDateIso (value) {
      if (!value) return ''
      const text = String(value).trim()
      const german = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/)

      if (german) {
        return `${german[3]}-${String(german[2]).padStart(2, '0')}-${String(german[1]).padStart(2, '0')}`
      }

      const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (isoDate) {
        return isoDate[0]
      }

      const date = new Date(text)
      if (Number.isNaN(date.getTime())) return ''
      return date.toISOString().slice(0, 10)
    },

    normalizeCompareText (value) {
      return String(value || '')
        .toLowerCase()
        .replace(/(\d{1,2}\.\d{1,2}\.)/g, ' $1 ')
        .replace(/(sellorder|buyorder|stopsell|tax correction)/gi, ' $1 ')
        .replace(/\([^)]*\)/g, ' ')
        .replace(/[^a-z0-9äöüß]+/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },

    normalizeTradeRepublicCompareRow (row) {
      const parseAmount = value => String(value ?? '').trim() === '' ? NaN : Number(value)
      const amount = parseAmount(row.amount) +
        (String(row.fee || '').trim() ? parseAmount(row.fee) : 0) +
        (String(row.tax || '').trim() ? parseAmount(row.tax) : 0)
      const orderType = { BUY: 'BuyOrder', SELL: 'SellOrder', TAX_OPTIMIZATION: 'Tax correction' }[row.type]
      const name = String(row.name || '').trim()
      const purpose = `${orderType} ${name} ${row.description || ''}`.trim()
      return {
        raw: row,
        date: this.parseAnyDateIso(row.date),
        amount,
        absAmount: Math.abs(amount),
        name,
        purpose,
        text: this.normalizeCompareText(`${name} ${purpose}`),
      }
    },

    normalizeBankCompareRow (row) {
      const date = this.parseAnyDateIso(row.wertstellung)
      const amount = this.parseGermanNumber(row.betrag)
      const name = String(row.empfaenger_auftraggeber || '').trim()
      const purpose = String(row.verwendungszweck || '').trim()

      return {
        raw: row,
        date,
        amount,
        absAmount: Math.abs(Number(amount || 0)),
        name,
        purpose,
        text: this.normalizeCompareText(`${name} ${purpose}`),
      }
    },

    isWhitelistedBankPurpose (purpose) {
      const text = String(purpose || '').toLowerCase()
      return [
        'sellorder',
        'buyorder',
        'tax correction',
        'stopsell',
      ].some(term => text.includes(term))
    },

    normalizeTransactionForBankCompare (tx) {
      const category = Number(tx?.stocks_transactions_category ?? 0)
      const rawAmount = Number(tx?.stocks_transactions_preisV02 ?? tx?.stocks_transactions_preis ?? 0)
      let signedAmount = rawAmount

      if (category === 1) signedAmount = -Math.abs(rawAmount)
      if (category === 2 || category === 3) signedAmount = Math.abs(rawAmount)

      const name = String(tx?.stocks_transactions_stocks_name || '').trim()
      const beschreibung = String(tx?.stocks_transactions_beschreibung || '').trim()

      return {
        raw: tx,
        id: this.extractTransactionId(tx),
        date: this.parseAnyDateIso(tx?.stocks_transactions_datum),
        amount: signedAmount,
        absAmount: Math.abs(Number(signedAmount || 0)),
        name,
        category,
        description: beschreibung,
        text: this.normalizeCompareText(`${name} ${beschreibung}`),
      }
    },

    debugBankCompareMatch (bankRows, appRows, pattern) {
      const needle = String(pattern || '').toLowerCase()
      if (!needle) return

      const bankMatches = bankRows.filter((row) =>
        String(row?.purpose || '').toLowerCase().includes(needle)
      )
      const appMatches = appRows.filter((row) =>
        String(row?.description || '').toLowerCase().includes(needle) ||
        String(row?.name || '').toLowerCase().includes('broadcom')
      )

      console.log('bank compare debug', {
        pattern,
        bankMatches,
        appMatches,
      })
    },

    debugBankCompareAppAssignment (bankRows, resultRows, appId, date) {
      console.log('bank compare assignment debug', {
        appId,
        date,
        bankRowsOnDate: bankRows.filter((row) => row.date === date),
        resultRowsForApp: resultRows.filter((row) => Number(row?.appId || 0) === Number(appId || 0)),
        missingBankRowsOnDate: resultRows.filter((row) =>
          row.status === 'Fehlt in App' &&
          row.bankDate === date
        ),
      })
    },

    debugBankCompareTransaction (bankRows, appRows, appId, pattern) {
      const appRow = appRows.find((row) => Number(row?.id || 0) === Number(appId || 0))
      const needle = String(pattern || '').toLowerCase()
      const bankMatches = bankRows.filter((row) =>
        String(row?.purpose || '').toLowerCase().includes(needle) ||
        String(row?.name || '').toLowerCase().includes(needle)
      )

      console.log('bank compare tx debug', {
        appId,
        appRow,
        bankMatches: bankMatches.map((bankRow) => ({
          bankRow,
          candidate: appRow ? this.buildBankCompareCandidate(bankRow, appRow) : null,
          bankOrderType: this.getCompareOrderTypeFromText(bankRow?.purpose),
          appOrderType: appRow
            ? this.getCompareOrderTypeFromText(appRow?.description) || this.getCompareOrderTypeFromCategory(appRow?.category)
            : '',
          stockAliases: appRow ? this.getStockMatchAliases(appRow?.name) : [],
          normalizedBankPurpose: this.normalizeStockMatchText(bankRow?.purpose),
        })),
      })
      console.table(bankMatches.map((bankRow) => {
        const candidate = appRow ? this.buildBankCompareCandidate(bankRow, appRow) : null
        return {
          appId,
          appDate: appRow?.date || '',
          appAmount: appRow?.amount ?? null,
          appName: appRow?.name || '',
          appCategory: appRow?.category ?? null,
          bankDate: bankRow?.date || '',
          bankAmount: bankRow?.amount ?? null,
          bankPurpose: bankRow?.purpose || '',
          dateMatch: candidate?.dateMatch ?? null,
          amountDiff: candidate?.amountDiff ?? null,
          absAmountDiff: candidate?.absAmountDiff ?? null,
          tolerance: this.getBankCompareTolerance(),
          specificTextMatch: candidate?.specificTextMatch ?? null,
          textMatch: candidate?.textMatch ?? null,
          bankOrderType: this.getCompareOrderTypeFromText(bankRow?.purpose),
          appOrderType: appRow
            ? this.getCompareOrderTypeFromText(appRow?.description) || this.getCompareOrderTypeFromCategory(appRow?.category)
            : '',
          stockAliases: appRow ? this.getStockMatchAliases(appRow?.name).join(',') : '',
          normalizedBankPurpose: this.normalizeStockMatchText(bankRow?.purpose),
        }
      }))
    },

    debugBankCompareResult (resultRows, appId, pattern) {
      const needle = String(pattern || '').toLowerCase()
      const rowsForApp = resultRows.filter((row) => Number(row?.appId || 0) === Number(appId || 0))
      const rowsForPattern = resultRows.filter((row) =>
        String(row?.bankPurpose || '').toLowerCase().includes(needle) ||
        String(row?.appName || '').toLowerCase().includes(needle)
      )
      console.log('bank compare result debug', {
        appId,
        rowsForApp,
        rowsForPattern,
      })
      console.table(rowsForPattern.map((row) => ({
        id: row?.id,
        status: row?.status,
        appId: row?.appId,
        bankDate: row?.bankDate,
        bankAmount: row?.bankAmount,
        bankPurpose: row?.bankPurpose,
        appDate: row?.appDate,
        appAmount: row?.appAmount,
        appName: row?.appName,
        appCategory: row?.appCategory,
        diff: row?.diff,
        note: row?.note,
      })))
    },

    normalizeBankCompareTableRow (row) {
      return (
        row?.item?.raw ??
        row?.internalItem?.raw ??
        row?.item ??
        row?.raw ??
        row ??
        null
      )
    },

    normalizeStockMatchText (value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9äöüß]+/gi, '')
        .trim()
    },

    findStockSuggestionForBankPurpose (purpose) {
      const purposeText = this.normalizeStockMatchText(purpose)
      if (!purposeText) return null

      const currentUserId = this.currentUserId
      const candidates = (Array.isArray(this.stocks) ? this.stocks : [])
        .filter((stock) => !currentUserId || this.getStockUserId(stock) === currentUserId)
        .filter((stock) => Number(stock?.idstocks ?? 0) > 0)
        .map((stock) => {
          const stockName = String(stock?.stocks_name || '').trim()
          const fullName = this.normalizeStockMatchText(stockName)
          const firstToken = this.normalizeStockMatchText(stockName.split(/\s+/)[0])
          const fullMatch = fullName && purposeText.includes(fullName)
          const firstTokenMatch = firstToken.length >= 4 && purposeText.includes(firstToken)

          return {
            stock,
            score: fullMatch ? fullName.length + 1000 : firstTokenMatch ? firstToken.length : 0,
          }
        })
        .filter((candidate) => candidate.score > 0)
        .sort((a, b) => b.score - a.score)

      return candidates[0]?.stock || null
    },

    getBankPurposeCategory (purpose) {
      const text = String(purpose || '').toLowerCase()
      if (text.includes('buyorder')) return 1
      if (text.includes('sellorder') || text.includes('stopsell')) return 2
      if (text.includes('tax correction')) return 4
      return 0
    },

    buildBankMissingTransaction (row) {
      const category = this.getBankPurposeCategory(row?.bankPurpose)
      const amount = Math.abs(Number(row?.bankAmount ?? 0))
      const stock = this.findStockSuggestionForBankPurpose(row?.bankPurpose)
      this.suggestedBankCreateStock = stock

      return {
        idstocks_transactions: 0,
        stocks_transactions_category: category,
        stocks_idstocks: Number(stock?.idstocks ?? 0),
        stocks_user_iduser: this.currentUserId,
        stocks_menge: 0,
        stocks_gebuehr: 0,
        stocks_transactions_preisprostueck: amount,
        stocks_transactions_preis: amount,
        stocks_transactions_preisV02: amount,
        stocks_transactions_datum: row?.bankDate || '',
        stocks_transactions_beschreibung: row?.bankPurpose || '',
      }
    },

    async openBankMissingTransactionDialog (row) {
      const bankRow = this.normalizeBankCompareTableRow(row)
      if (bankRow?.status !== 'Fehlt in App') return

      if (!Array.isArray(this.stocks) || !this.stocks.length) {
        await this.getStocks()
      }

      this.bankCreateRow = bankRow
      this.bankCreateTx = this.buildBankMissingTransaction(bankRow)
      this.bankCreateError = ''
      this.bankCreateDialog = true
    },

    findTransactionByCompareRow (bankRow) {
      const appId = Number(bankRow?.appId || 0)
      if (!appId) return null

      const directTransaction = bankRow?.appTransaction
      if (directTransaction && this.extractTransactionId(directTransaction) === appId) {
        return directTransaction
      }

      return (Array.isArray(this.transactions) ? this.transactions : [])
        .find((item) => this.extractTransactionId(item) === appId) || null
    },

    async openBankCompareTransactionEditor (row) {
      const bankRow = this.normalizeBankCompareTableRow(row)
      const appId = Number(bankRow?.appId || 0)
      if (!appId) {
        console.warn('bank compare click without appId', bankRow)
        return false
      }

      let tx = this.findTransactionByCompareRow(bankRow)

      if (!tx) {
        await this.$store.dispatch('getTransactions')
        tx = this.findTransactionByCompareRow(bankRow)
      }

      if (!tx) {
        console.warn('bank compare transaction not found', { appId, bankRow })
        return false
      }

      this.selectedTx = tx
      this.editedTx = this.buildEditableTransaction(tx)
      this.detailsDialog = true
      return true
    },

    async handleClickBankCompareRow (event, row) {
      const bankRow = this.normalizeBankCompareTableRow(row)
      if (bankRow?.status === 'Fehlt in App') {
        this.openBankMissingTransactionDialog(bankRow)
        return
      }

      await this.openBankCompareTransactionEditor(bankRow)
    },

    closeBankCreateDialog () {
      this.bankCreateDialog = false
      this.bankCreateRow = null
      this.bankCreateTx = null
      this.bankCreateError = ''
      this.suggestedBankCreateStock = null
    },

    async saveBankMissingTransaction () {
      if (!this.bankCreateTx) return

      if (!Number(this.bankCreateTx.stocks_idstocks || 0)) {
        this.bankCreateError = 'Bitte einen Stock auswaehlen.'
        return
      }

      try {
        this.bankCreateSaving = true
        this.bankCreateError = ''

        await axios.post('/manualtransaction', {
          stocks_transactions_category: Number(this.bankCreateTx.stocks_transactions_category || 0),
          stocks_idstocks: Number(this.bankCreateTx.stocks_idstocks || 0),
          stocks_menge: Number(this.bankCreateTx.stocks_menge || 0),
          stocks_gebuehr: Number(this.bankCreateTx.stocks_gebuehr || 0),
          stocks_transactions_preisprostueck: Number(this.bankCreateTx.stocks_transactions_preisprostueck || 0),
          stocks_transactions_preis: Number(this.bankCreateTx.stocks_transactions_preis || 0),
          stocks_transactions_preisV02: Number(this.bankCreateTx.stocks_transactions_preisV02 || 0),
          stocks_transactions_datum: this.bankCreateTx.stocks_transactions_datum || '',
          stocks_transactions_beschreibung: this.bankCreateTx.stocks_transactions_beschreibung || '',
        })

        await this.$store.dispatch('getTransactions')
        this.closeBankCreateDialog()
        this.queueBarChartRender()
      } catch (error) {
        console.error('saveBankMissingTransaction failed', error)
        this.bankCreateError =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Transaktion konnte nicht gespeichert werden.'
      } finally {
        this.bankCreateSaving = false
      }
    },

    getBankCompareTolerance () {
      if (Number.isFinite(this.bankCompareTolerance)) return Number(this.bankCompareTolerance)

      const normalized = String(this.bankCompareTolerance ?? '')
        .trim()
        .replace(',', '.')
      const tolerance = Number(normalized)
      return Number.isFinite(tolerance) ? tolerance : 0
    },
    async compareBankRowsWithTransactions (bankRows, appRows) {
      const tolerance = this.getBankCompareTolerance()
      const matchCandidates = []
      const preparedAppRows = appRows.map(appRow => ({
        appRow,
        stockName: this.normalizeStockMatchText(appRow.name),
        aliases: this.getStockMatchAliases(appRow.name),
        orderType: this.getCompareOrderTypeFromText(appRow.description) || this.getCompareOrderTypeFromCategory(appRow.category),
      }))

      for (let bankIndex = 0; bankIndex < bankRows.length; bankIndex += 1) {
        // Yield regularly so loading feedback, scrolling and browser controls remain responsive.
        if (bankIndex % 20 === 0) await new Promise(resolve => setTimeout(resolve, 0))
        const bankRow = bankRows[bankIndex]
        const bankPurpose = this.normalizeStockMatchText(bankRow.purpose)
        const bankOrderType = this.getCompareOrderTypeFromText(bankRow.purpose)
        preparedAppRows.forEach(({ appRow, stockName, aliases, orderType }) => {
          const specificTextMatch = (bankOrderType === 'tax' && (orderType === 'tax' || stockName === 'steuer')) ||
            (bankOrderType && orderType && bankOrderType === orderType && aliases.some(alias => bankPurpose.includes(alias)))
          // Every existing matching priority requires this condition.
          if (!specificTextMatch) return
          const candidate = {
            appRow,
            dateMatch: appRow.date === bankRow.date,
            amountDiff: Math.abs(Number(appRow.amount) - Number(bankRow.amount)),
            absAmountDiff: Math.abs(Number(appRow.absAmount) - Number(bankRow.absAmount)),
            specificTextMatch,
          }
          const dateAmountMatch = candidate.dateMatch &&
            (
              candidate.amountDiff <= tolerance ||
              candidate.absAmountDiff <= tolerance
            )

          let priority = 0
          if (candidate.dateMatch && candidate.amountDiff <= tolerance && candidate.specificTextMatch) {
            priority = 400
          } else if (dateAmountMatch && candidate.specificTextMatch) {
            priority = 300
          } else if (candidate.specificTextMatch && candidate.absAmountDiff <= tolerance) {
            priority = 200
          } else if (candidate.specificTextMatch) {
            priority = 150
          }

          if (!priority) return

          matchCandidates.push({
            ...candidate,
            bankIndex,
            bankRow,
            appKey: this.getBankCompareAppKey(appRow),
            priority,
          })
        })
      }

      matchCandidates.sort((a, b) => {
        if (a.priority !== b.priority) return b.priority - a.priority
        if (a.dateMatch !== b.dateMatch) return Number(b.dateMatch) - Number(a.dateMatch)
        if (a.amountDiff !== b.amountDiff) return a.amountDiff - b.amountDiff
        if (a.absAmountDiff !== b.absAmountDiff) return a.absAmountDiff - b.absAmountDiff
        return a.bankIndex - b.bankIndex
      })

      const usedBankIndexes = new Set()
      const usedAppIds = new Set()
      const matchesByBankIndex = new Map()

      matchCandidates.forEach((candidate) => {
        if (usedBankIndexes.has(candidate.bankIndex) || usedAppIds.has(candidate.appKey)) return

        usedBankIndexes.add(candidate.bankIndex)
        usedAppIds.add(candidate.appKey)
        candidate.textMatch = this.hasCompareTextOverlap(candidate.bankRow.text, candidate.appRow.text)
        matchesByBankIndex.set(candidate.bankIndex, candidate)
      })

      const resultRows = bankRows.map((bankRow, index) =>
        this.buildBankCompareResultRow(bankRow, matchesByBankIndex.get(index) || null, index)
      )

      const missingBankRows = appRows
        .filter(appRow => !usedAppIds.has(this.getBankCompareAppKey(appRow)))
        .map((appRow, index) => ({
          id: `app-${index}`,
          status: 'Fehlt im Bank-Export',
          bankDate: '',
          bankName: '',
          bankPurpose: '',
          bankAmount: null,
          appId: appRow.id,
          appTransaction: appRow.raw,
          appDate: appRow.date,
          appName: appRow.name,
          appCategory: appRow.category,
          appAmount: appRow.amount,
          diff: null,
          note: 'App-Transaktion wurde keiner Bank-Zeile zugeordnet.',
        }))

      return [...resultRows, ...missingBankRows]
    },
    buildBankCompareCandidate (bankRow, appRow) {
      return {
        appRow,
        dateMatch: appRow.date === bankRow.date,
        amountDiff: Math.abs(Number(appRow.amount) - Number(bankRow.amount)),
        absAmountDiff: Math.abs(Number(appRow.absAmount) - Number(bankRow.absAmount)),
        textMatch: this.hasCompareTextOverlap(bankRow.text, appRow.text),
        specificTextMatch: this.hasSpecificCompareTextMatch(bankRow, appRow),
      }
    },

    getBankCompareAppKey (appRow) {
      return appRow.id || `${appRow.date}-${appRow.amount}-${appRow.name}-${appRow.category}`
    },

    getCompareOrderTypeFromText (value) {
      const text = String(value || '').toLowerCase()
      const compactText = this.normalizeStockMatchText(value)
      if (text.includes('buyorder') || compactText.includes('buyorder')) return 'buy'
      if (text.includes('sellorder') || text.includes('stopsell') || compactText.includes('sellorder') || compactText.includes('stopsell')) return 'sell'
      if (text.includes('tax correction') || compactText.includes('taxcorrection')) return 'tax'
      return ''
    },

    getCompareOrderTypeFromCategory (category) {
      const numericCategory = Number(category || 0)
      if (numericCategory === 1) return 'buy'
      if (numericCategory === 2) return 'sell'
      if (numericCategory === 4) return 'tax'
      return ''
    },

    getStockMatchAliases (name) {
      const rawName = String(name || '').trim()
      const normalizedName = this.normalizeStockMatchText(rawName)
      const words = rawName
        .split(/[^a-zA-Z0-9äöüÄÖÜß]+/)
        .map(word => word.trim())
        .filter(Boolean)
      const firstToken = this.normalizeStockMatchText(words[0] || '')
      const acronym = this.normalizeStockMatchText(words.map(word => word[0]).join(''))

      return [normalizedName, firstToken, acronym]
        .filter(alias => alias.length >= 3)
        .filter((alias, index, aliases) => aliases.indexOf(alias) === index)
    },

    hasSpecificCompareTextMatch (bankRow, appRow) {
      const stockName = this.normalizeStockMatchText(appRow?.name)
      const stockAliases = this.getStockMatchAliases(appRow?.name)
      const bankPurpose = this.normalizeStockMatchText(bankRow?.purpose)
      const bankOrderType = this.getCompareOrderTypeFromText(bankRow?.purpose)
      const appOrderType =
        this.getCompareOrderTypeFromText(appRow?.description) ||
        this.getCompareOrderTypeFromCategory(appRow?.category)

      if (bankOrderType === 'tax' && (appOrderType === 'tax' || stockName === 'steuer')) {
        return true
      }

      return (
        stockAliases.some(alias => bankPurpose.includes(alias)) &&
        bankOrderType &&
        appOrderType &&
        bankOrderType === appOrderType
      )
    },

    hasCompareTextOverlap (left, right) {
      const ignoredTokens = new Set(['buyorder', 'sellorder', 'stopsell', 'tax', 'correction'])
      const toSpecificTokens = (value) => String(value || '')
        .split(' ')
        .filter(token =>
          token.length >= 4 &&
          !ignoredTokens.has(token) &&
          !/^\d+$/.test(token)
        )
      const leftTokens = toSpecificTokens(left)
      const rightTokens = toSpecificTokens(right)

      return leftTokens.some(leftToken =>
        rightTokens.some(rightToken =>
          leftToken === rightToken ||
          leftToken.startsWith(rightToken) ||
          rightToken.startsWith(leftToken)
        )
      )
    },

    buildBankCompareResultRow (bankRow, match, index) {
      if (!match?.appRow) {
        return {
          id: `bank-${index}`,
          status: 'Fehlt in App',
          bankDate: bankRow.date,
          bankName: bankRow.name,
          bankPurpose: bankRow.purpose,
          bankAmount: bankRow.amount,
          appId: null,
          appDate: '',
          appName: '',
          appCategory: null,
          appAmount: null,
          diff: null,
          note: 'Keine Transaktion am gleichen Datum gefunden.',
        }
      }

      const appRow = match.appRow
      const tolerance = this.getBankCompareTolerance()
      const amountMatches = match.amountDiff <= tolerance
      const exact = match.dateMatch && amountMatches && match.specificTextMatch
      const dateDeviation = !match.dateMatch && amountMatches && match.specificTextMatch
      const status = exact ? 'OK' : dateDeviation ? 'Abweichung Datum' : 'Abweichung'

      return {
        id: `bank-${index}`,
        status,
        bankDate: bankRow.date,
        bankName: bankRow.name,
        bankPurpose: bankRow.purpose,
        bankAmount: bankRow.amount,
        appId: appRow.id,
        appTransaction: appRow.raw,
        appDate: appRow.date,
        appName: appRow.name,
        appCategory: appRow.category,
        appAmount: appRow.amount,
        diff: Number((Number(appRow.amount) - Number(bankRow.amount)).toFixed(2)),
        note: match.dateMatch
          ? match.textMatch ? 'Text-Treffer' : 'Datum/Betrag ohne klaren Text-Treffer'
          : 'Text/Betrag-Treffer ohne gleiches Datum',
      }
    },

    buildBankCompareSummary (rows, bankCount, appCount) {
      return {
        bankRows: bankCount,
        appRows: appCount,
        exactMatches: rows.filter(row => row.status === 'OK').length,
        partialMatches: rows.filter(row => row.status === 'Betrag OK' || row.status === 'Abweichung' || row.status === 'Abweichung Datum').length,
        missingInApp: rows.filter(row => row.status === 'Fehlt in App').length,
        missingInBank: rows.filter(row => row.status === 'Fehlt im Bank-Export').length,
      }
    },

    bankCompareStatusColor (status) {
      if (status === 'OK') return 'success'
      if (status === 'Betrag OK') return 'info'
      if (status === 'Abweichung' || status === 'Abweichung Datum') return 'warning'
      return 'error'
    },

    downloadBankCompareCsv () {
      if (!this.bankCompareRows.length) return

      const headers = this.bankCompareHeaders.map(header => ({
        title: header.title,
        key: header.key,
      }))
      const lines = [
        headers.map(header => this.escapeCsvCell(header.title)).join(';'),
        ...this.bankCompareRows.map(row =>
          headers
            .map(header => this.escapeCsvCell(row?.[header.key] ?? ''))
            .join(';')
        ),
      ]
      const csvContent = `\uFEFF${lines.join('\r\n')}`
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const today = new Date().toISOString().slice(0, 10)

      link.href = url
      link.download = `banking_vergleich_${today}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    extractTransactionId (item) {
      const source = item || {}
      const candidates = [
        source.idstocks_transactions,
        source.transaction?.idstocks_transactions,
        source.transaction_id,
        source.idtransactions,
        source.stocks_transactions_id,
        source.transactions_id,
        source.id,
        source.transaction?.id,
      ]

      const found = candidates.find((value) => Number.isFinite(Number(value)) && Number(value) > 0)
      return found != null ? Number(found) : null
    },

    normalizeTransactionRow (row) {
      return (
        row?.item?.raw ??
        row?.internalItem?.raw ??
        row?.item ??
        row?.raw ??
        row ??
        null
      )
    },

    buildEditableTransaction (tx) {
      const source = tx || {}
      return {
        idstocks_transactions: this.extractTransactionId(source) ?? 0,
        stocks_transactions_category: Number(source.stocks_transactions_category ?? 0),
        stocks_idstocks: Number(source.stocks_idstocks ?? 0),
        stocks_user_iduser: Number(source.stocks_user_iduser ?? 0),
        stocks_menge: Number(source.stocks_menge ?? 0),
        stocks_gebuehr: Number(source.stocks_gebuehr ?? 0),
        stocks_transactions_preisprostueck: Number(
          source.stocks_transactions_preisprostueck ??
          source.stocks_transactions_preis ??
          0
        ),
        stocks_transactions_preis: Number(source.stocks_transactions_preis ?? 0),
        stocks_transactions_preisV02: Number(source.stocks_transactions_preisV02 ?? 0),
        stocks_transactions_datum: source.stocks_transactions_datum
          ? String(source.stocks_transactions_datum).slice(0, 10)
          : '',
        stocks_transactions_beschreibung: source.stocks_transactions_beschreibung ?? '',
      }
    },

    handleClickActivty (event, row) {
      const tx = this.normalizeTransactionRow(row)

      console.log('Row clicked tx:', tx)

      this.selectedTx = tx
      this.editedTx = this.buildEditableTransaction(tx)
      this.detailsDialog = true
    },

    closeDetailsDialog () {
      this.detailsDialog = false
      this.selectedTx = null
      this.editedTx = null
    },

    async saveSelectedTransaction () {
      const txId = this.extractTransactionId(this.editedTx || this.selectedTx)
      if (!txId || !this.editedTx) return

      try {
        this.detailsSaving = true
        await this.updateTransaction({
          id: txId,
          form: { ...this.editedTx },
        })

        await this.$store.dispatch('getTransactions')
        this.closeDetailsDialog()
        this.queueBarChartRender()
      } catch (error) {
        console.error('saveSelectedTransaction failed', error)
      } finally {
        this.detailsSaving = false
      }
    },

    async deleteSelectedTransaction () {
      const txId = this.extractTransactionId(this.selectedTx || this.editedTx)
      if (!txId) return

      const confirmed = window.confirm(`Transaktion ${txId} wirklich löschen?`)
      if (!confirmed) return

      try {
        this.detailsDeleting = true
        await this.deleteTransaction(txId)
        await this.$store.dispatch('getTransactions')
        this.closeDetailsDialog()
        this.queueBarChartRender()
      } catch (error) {
        console.error('deleteSelectedTransaction failed', error)
      } finally {
        this.detailsDeleting = false
      }
    },

    handleClickStrategyRow (event, row) {
      const strategyRow =
        row?.item?.raw ??
        row?.item ??
        row?.raw ??
        row

      if (!strategyRow?.stock_id) return

      this.$router.push({
        name: 'Analyzer',
        params: {
          idstocks: strategyRow.stock_id,
          stocks_name: strategyRow.stock_name,
        },
      })
    },

    handleTaxCsvImported () {
      this.queueBarChartRender()
    },

    async loadStrategyComparison () {
      this.strategyLoading = true
      this.strategyError = ''

      try {
        const response = await axios.get('/deltapreis/all')
        const rows = Array.isArray(response?.data) ? response.data : []

        this.strategyRows = rows
          .map((item) => ({
            ...item,
            actual_net_result: Number(item?.actual_strategy?.net_result ?? 0),
            benchmark_net_result: Number(item?.buy_and_hold_benchmark?.net_result ?? 0),
            outperformance_eur: Number(item?.outperformance_eur ?? 0),
            outperformance_pct_points: Number(item?.outperformance_pct_points ?? 0),
            trade_count: Number(item?.trade_count ?? 0),
          }))
          .sort((a, b) => (b.outperformance_eur || 0) - (a.outperformance_eur || 0))
      } catch (error) {
        console.error('loadStrategyComparison error', error)
        this.strategyError =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Strategie-Vergleich konnte nicht geladen werden.'
      } finally {
        this.strategyLoading = false
      }
    },

    formatCurrency (value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
      }).format(num)
    },

    formatDate (value) {
      if (!value) return '-'
      const parts = this.parseDbDateTimeParts(value)
      if (parts) return `${parts.day}.${parts.month}.${parts.year}`
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '-'
      return d.toLocaleDateString('de-DE')
    },

    formatDateTime (value) {
      if (!value) return '-'
      const parts = this.parseDbDateTimeParts(value)
      if (parts) return `${parts.day}.${parts.month}.${parts.year}, ${parts.hour}:${parts.minute}`
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '-'
      return d.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    parseDbDateTimeParts (value) {
      const text = String(value || '').trim()
      const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?/)
      if (!match) return null

      return {
        year: match[1],
        month: match[2],
        day: match[3],
        hour: match[4] || '00',
        minute: match[5] || '00',
      }
    },

    formatDetailLabel (key) {
      const labels = {
        id: 'ID',
        stocks_idstocks: 'Stock ID',
        stocks_transactions_stocks_name: 'Stock Name',
        stocks_transactions_category: 'Typ',
        stocks_menge: 'Menge',
        stocks_transactions_preis: 'Preis',
        stocks_transactions_datum: 'Datum',
        stocks_transactions_beschreibung: 'Beschreibung',
      }

      return labels[key] || key
    },

    formatDetailValue (key, value) {
      if (value === null || value === '') return '-'

      const normalizedKey = String(key || '').toLowerCase()
      if (normalizedKey.includes('datum') || normalizedKey.includes('date')) {
        return this.formatDate(value)
      }

      if (typeof value === 'boolean') {
        return value ? 'Ja' : 'Nein'
      }

      if (typeof value === 'object') {
        return JSON.stringify(value)
      }

      return String(value)
    },

    formatPercentPoint (value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return `${num.toFixed(2)}`
    },

    formatStrategyLabel (value) {
      if (value === 'actual_strategy') return 'Aktiv'
      if (value === 'buy_and_hold_benchmark') return 'Buy & Hold'
      return 'Unklar'
    },

    numberClass (value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return ''
      if (num > 0) return 'text-success'
      if (num < 0) return 'text-error'
      return ''
    },

    async queueBarChartRender () {
      if (this.tab !== 'chart') return

      await this.$nextTick()
      if (this.tab !== 'chart') return

      if (this.chartRenderFrame !== null) {
        window.cancelAnimationFrame(this.chartRenderFrame)
      }
      this.chartRenderFrame = window.requestAnimationFrame(() => {
        this.chartRenderFrame = null
        if (this.tab === 'chart') this.renderBarChart()
      })
    },

    renderBarChart () {
      if (this.tab !== 'chart') return
      const el = this.$refs.barCanvas
      if (!el || !this.transactions || !this.transactions.length) return

      // alten Chart zerstören
      if (this.chartBar) {
        this.chartBar.destroy()
        this.chartBar = null
      }

      console.log('transactions', this.transactions)

      // --- Aggregation nach Aktie + Typ (2 = Verkauf, 3 = Dividende) ---
      const aggregated = this.transactions.reduce((acc, t) => {
        const name = t.stocks_transactions_stocks_name || 'Unbekannt'
        const preis = Number(t.stocks_transactions_preis)
        const cat = String(t.stocks_transactions_category).trim() // "2" oder "3"

        if (Number.isNaN(preis)) return acc

        if (!acc[name]) {
          acc[name] = { dividende: 0, verkauf: 0 }
        }

        if (cat === '3') {
          acc[name].dividende += preis
        } else if (cat === '2') {
          acc[name].verkauf += preis
        }

        return acc
      }, {})

      // in sortierte Liste umwandeln (nach Gesamtbetrag)
      const entries = Object.entries(aggregated)
        .map(([name, { dividende, verkauf }]) => {
          const d = Math.round(dividende * 100) / 100
          const v = Math.round(verkauf * 100) / 100
          return {
            name,
            dividende: d,
            verkauf: v,
            total: d + v,
          }
        })
        .sort((a, b) => b.total - a.total)

      if (!entries.length) return

      const labels = entries.map(e => e.name)
      const dataDividende = entries.map(e => e.dividende)
      const dataVerkauf = entries.map(e => e.verkauf)

      const dataNeu = {
        labels,
        datasets: [
          {
            label: 'Typ 3 Dividende',
            data: dataDividende,
            xAxisID: 'x',
            stack: 'delta',
            backgroundColor: 'rgba(75, 192, 192, 0.6)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1,
          },
          {
            label: 'Typ 2 Verkauf',
            data: dataVerkauf,
            xAxisID: 'x',
            stack: 'delta',
            backgroundColor: 'rgba(255, 159, 64, 0.6)',
            borderColor: 'rgba(255, 159, 64, 1)',
            borderWidth: 1,
          },
        ],
      }

      // Achsenbereich anhand der gestackten Summen berechnen
      const positiveTotals = entries.map(e =>
        [e.dividende, e.verkauf].filter(v => v > 0).reduce((a, b) => a + b, 0)
      )
      const negativeTotals = entries.map(e =>
        [e.dividende, e.verkauf].filter(v => v < 0).reduce((a, b) => a + b, 0)
      )

      const maxVal = Math.max(0, ...positiveTotals)
      const minVal = Math.min(0, ...negativeTotals)

      const formatEuro = (val) =>
        new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        }).format(val)

      const ctx = el.getContext('2d')

      this.chartBar = new Chart(ctx, {
        type: 'bar',
        data: dataNeu,
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'linear',
              position: 'top',
              stacked: true,
              min: minVal,
              max: maxVal,
              ticks: {
                callback: (value) => formatEuro(value),
              },
              grid: {
                drawOnChartArea: false,
              },
            },
            x2: {
              type: 'linear',
              position: 'bottom',
              stacked: true,
              min: minVal,
              max: maxVal,
              ticks: {
                callback: (value) => formatEuro(value),
              },
              grid: {
                drawOnChartArea: false,
              },
            },
            y: {
              stacked: true,
              ticks: {
                autoSkip: false,
              },
            },
          },
          plugins: {
            legend: {
              display: true,
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const datasetLabel = ctx.dataset.label || ''
                  const val = ctx.parsed.x ?? 0
                  const euro = formatEuro(val)
                  return `${datasetLabel}: ${euro}`
                },
              },
            },
          },
        },
      })
    },
  },
}
</script>

<style scoped>
.strategy-comparison-table {
  max-width: 100%;
}

.strategy-comparison-table :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 2;
}

.strategy-comparison-table :deep(thead th) {
  background-color: inherit;
}

.tx-table :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 2;
}

.tx-table :deep(thead th) {
  background-color: inherit;
}

.tx-table :deep(tbody tr.tx-row-buy > td) {
  background-color: rgba(46, 125, 50, .22);
}

.tx-table :deep(tbody tr.tx-row-sell > td) {
  background-color: rgba(211, 47, 47, .22);
}

.tx-table :deep(tbody tr.tx-row-buy:hover > td) {
  background-color: rgba(46, 125, 50, .30);
}

.tx-table :deep(tbody tr.tx-row-sell:hover > td) {
  background-color: rgba(211, 47, 47, .30);
}

.tx-table :deep(tbody tr.tx-row-buy > td:first-child) {
  box-shadow: inset 4px 0 #43a047;
}

.tx-table :deep(tbody tr.tx-row-sell > td:first-child) {
  box-shadow: inset 4px 0 #e53935;
}

.tx-header-filter {
  min-width: 180px;
}

.tx-header-filter__title {
  font-weight: 600;
  line-height: 1.2;
}

.tx-header-filter__input {
  margin-top: 4px;
  min-width: 160px;
}

.pivot-scroll {
  max-height: calc(100vh - 260px);
  overflow: auto;
}

.pivot-scroll :deep(.pvtTable thead th) {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #212121;
}

.pivot-scroll :deep(.pvtTable thead tr:nth-child(2) th) {
  top: 42px;
}
</style>
